import React, { createContext, useCallback, useContext, useRef } from "react";

/**
 * Cross-subtree seam for triggering a coverage-grid refresh from outside the
 * grid. `CoverageView` and `UploadRail` are siblings on the Fay page and don't
 * share state; approve fires deep in the UploadRail tree but must nudge the
 * sibling grid to refetch. The grid registers its `refreshCoverage` here;
 * consumers call it.
 *
 * A ref-backed registry (not a piece of state) keeps CoverageView's own
 * `useCoverageData` re-renders from cascading through every consumer.
 *
 * Each host app wraps its Fay surface in `CoverageRefreshProvider` once.
 */
type RegisterFn = (fn: (() => void) | null) => void;
type RefreshFn = () => void;

const noopRegister: RegisterFn = () => undefined;
const noopRefresh: RefreshFn = () => undefined;

const RegisterContext = createContext<RegisterFn | null>(null);
const RefreshContext = createContext<RefreshFn | null>(null);

type Props = {
  children: React.ReactNode;
};

export const CoverageRefreshProvider: React.FC<Props> = ({ children }) => {
  const ref = useRef<(() => void) | null>(null);

  const register = useCallback<RegisterFn>((fn) => {
    ref.current = fn;
  }, []);

  const refresh = useCallback<RefreshFn>(() => {
    ref.current?.();
  }, []);

  // Split into two contexts so a consumer that only calls refresh() doesn't
  // re-render when the register identity changes. `register` and `refresh` are
  // `useCallback(_, [])` — stable for the provider's lifetime — so passing
  // them directly is safe; a `useMemo` wrapper would be a no-op.
  return (
    <RegisterContext.Provider value={register}>
      <RefreshContext.Provider value={refresh}>{children}</RefreshContext.Provider>
    </RegisterContext.Provider>
  );
};

/** Registers the grid's `refreshCoverage` for cross-subtree callers to invoke.
 *  No-op outside the provider (safe in tests that render pieces in isolation). */
export function useRegisterCoverageRefresh(): RegisterFn {
  const register = useContext(RegisterContext);
  return register ?? noopRegister;
}

/** Triggers a coverage-grid refresh from outside the grid subtree. No-op if
 *  the grid isn't mounted or the provider is absent. */
export function useCoverageRefresh(): RefreshFn {
  const refresh = useContext(RefreshContext);
  return refresh ?? noopRefresh;
}
